<template>
    <div>
        <data-table v-if="ready" class="table table-striped" ref="table"
            :columns="columns"
            :data="issues"
            :loading="loading"
            :options="{ edit: { enabled: true, iconButton: true } }"
            @edit="startEdit($event)"
            @comments="loadComments($event)"
            @external="linkToJira($event)"
        />
        <edit v-if="users && issueToEdit"
            :issue="issueToEdit"
            :users="users"
            @update-issue="updateIssue($event)"
            @stop-edit="stopEdit()"
        />
        <comments v-if="users" 
            :users="users" 
            ref="comments"
        />
    </div>
</template>

<script>

//Util
import api from '../api';
import {fields} from "../Util/jira";
import growler, { bootstrapDangerGrowl } from '../../../Util/growler';
import {secondsToJiraTimeString as timeString} from '../../../Util/time';
import {defaultContentType} from '../../../Components/Util/DataTable/DataTable.vue';
import _ from 'lodash';
import $ from 'jquery';

//Mixins
import resourceMixin from '../../../Util/Mixins/resources';

//Components
import DataTable from '../../../Components/Util/DataTable/DataTable.vue';
import Edit from './Edit.vue';
import Comments from './Comments.vue';

export default {
    name: 'sprint-planning-sprint',
    mixins: [ resourceMixin ],
    components: {
        DataTable,
        Edit,
        Comments
    },
    created() {
        const vm = this;

        const issueTypesPromise = api.getIssueTypes().then(
            issueTypes => { vm.issueTypes = issueTypes; },
            growler.bootstrapDanger
        );
        
        const usersPromise = api.getUsers().then(
            (users) => {
                vm.users = users.filter(
                    user => user.accountType != 'app'
                );
            },
            bootstrapDangerGrowl
        );
        
        //TODO: replace with Promise.allSettled once proposal is finalized and polyfilled within our project
        const promises = [
            issueTypesPromise,
            usersPromise,
            api.validateCommentsReadPermission().then(canRead => vm.canReadComments = canRead),
            api.validateCommentsWritePermission().then(canWrite => vm.canWriteComments = canWrite),
            api.validateTimeTrackingReadPermission().then(canRead => vm.canReadTimeTracking = canRead)
        ];
        
        const promCount = promises.length;
        let completedProms = 0;
        
        const promiseCompletionHandler = () => { completedProms++; vm.ready = completedProms === promCount; };
        promises.forEach(p => p.then(promiseCompletionHandler, promiseCompletionHandler));
    },
    methods: {
        linkToJira(issue) {

            const url = api.jiraBaseUrl + issue.issueId;
            openInNewTab(url);

            function openInNewTab(url) {
                var win = window.open(url, '_blank');
                win.focus();
            }
        },
        loadComments(issue) {
            this.$refs.comments.load(issue);
        },
        updateIssue(changes) {
            const vm = this;
            const index = vm.issues.findIndex((target) => target.issueId === changes.issueId);
            
            //Update our issue with made changes
            vm.issues[index] = _.merge({}, vm.issues[index], changes);
            vm.$refs.table.setRowData(vm.issues[index]);
        },
        startEdit(issue) {
            const targetIssue = this.issues.find((target) => target.issueId === issue.issueId);
            this.issueToEdit = Object.assign({}, targetIssue); //We want a copy.
        },
        stopEdit() {
            this.$refs.table.finishEdit();
            this.issueToEdit = null;
        },
        refresh() {
            if(this.filterType) {
                this.getSprint(this.filterType);
            }
        },
        getSprint(filterType) {
            const vm = this;
            vm.loading = true;
            
            getBatch().then(
                vm.mapIssues,
                (error) => {
                    bootstrapDangerGrowl(error);
                    vm.loading = false;
                }
            )

            function getBatch(sprint = []) {
                return new Promise((resolve,reject) => {
                    api.getIssues(filterType,sprint.length).then(
                        (response) => {
                            sprint = sprint.concat(response.issues);
                            
                            //if there are more issues to get. call ourself recursively.
                            if(sprint.length < response.total) {
                                getBatch(sprint).then(resolve).catch(reject)
                            } else {
                                resolve(sprint);
                            }
                        },
                        reject
                    );
                });
            }
        },
        replaceNull(value, target, replacement) {
            return (value !== null && value != undefined) 
                ? value[target] 
                : replacement;
        },
        mapIssues(issues) {
            const vm = this;
            vm.issues = issues.map((issue) => {
                const issueModel = {
                    sprint: fields.sprint.parse(issue.fields[fields.sprint.name]),
                    issueId: issue.key,
                    type: vm.issueTypes.find(
                        type => type.id === issue.fields.issuetype.id
                    ).name,
                    summary: issue.fields.summary,
                    description: issue.fields.description,
                    status: issue.fields.status,
                    priority: issue.fields.priority,
                    weight: issue.fields.customfield_11108 || null,
                    assignee: issue.fields.assignee,
                    requestedBy: issue.fields.customfield_11107 || null,
                    DT_RowId: issue.key
                };
                
                if(vm.canReadTimeTracking){
                    issueModel.estimatedSeconds = issue.fields.timeoriginalestimate;
                    issueModel.remainingSeconds = issue.fields.timeestimate;
                    issueModel.loggedSeconds = issue.fields.timespent;
                    
                    issueModel.estimated = timeString(issue.fields.timeoriginalestimate);
                    issueModel.remaining = timeString(issue.fields.timeestimate);
                    issueModel.logged = timeString(issue.fields.timespent);
                }
                
                return issueModel;
            });
            vm.loading = false;
        }
    },
    computed: {
        columns() {
            const vm = this;
            const columns = [];

            const commentBtn =  `
                <a href="javascript:void(0)" class="mdb-icon-btn">
                    <i class="fas fa-comments"></i>
                </a>`;

            const openInJiraBtn = `
                <a href="javascript:void(0)" class="mdb-icon-btn">
                    <i class="fas fa-external-link-alt"></i>
                </a>`;
            
            columns.push(
                { title: this.resource('SprintPlanning_Issue_Key_DisplayName'), data: 'issueId'},
                { title: this.resource('SprintPlanning_Issue_Type_DisplayName'), data: 'type'},
                { title: this.resource('SprintPlanning_Issue_Summary_DisplayName'), data: 'summary', responsivePriority: -1 },
                { title: this.resource('SprintPlanning_Issue_Priority_DisplayName'), data: 'priority.name'},
                { title: this.resource('SprintPlanning_Issue_Weight_DisplayName'), data: 'weight.value', defaultContent: "-"},
                { title: this.resource('SprintPlanning_Issue_AssignedTo_DisplayName'), data: 'assignee.displayName', defaultContent: "unassigned"},
                { title: this.resource('SprintPlanning_Issue_RequestedBy_DisplayName'), data: 'requestedBy.value', defaultContent: "-" },
                { title: this.resource('SprintPlanning_Issue_Status_DisplayName'), data: 'status.name'},
                { title: this.resource('SprintPlanning_Issue_Sprint_DisplayName'), data: 'sprint.name' }
            );
            
            if(vm.canReadTimeTracking){
                columns.push(
                    { title: this.resource('SprintPlanning_Issue_Estimated_DisplayName'), data: 'estimated'},
                    { title: this.resource('SprintPlanning_Issue_Remaining_DisplayName'), data: 'remaining'},
                    { title: this.resource('SprintPlanning_Issue_Logged_DisplayName'), data: 'logged'}
                );
            }
            
            columns.push(
                { 
                    name: 'external', 
                    data: null, 
                    defaultContent: 
                    openInJiraBtn, 
                    type: defaultContentType.button, 
                    responsivePriority: -1 //Ensures it will never be hidden by responsive datatables
                    }
            );
            
            if(vm.canReadComments){
                columns.push(
                    { 
                        name: 'comments', 
                        data: null, 
                        defaultContent: 
                        commentBtn, 
                        type: defaultContentType.button, 
                        responsivePriority: -1  //Ensures it will never be hidden by responsive datatables
                    }
                )
            }
            
            columns.push(
                { data: 'description', visible: false},
                { data: 'assignee', visible: false },
                { data: 'priority', visible: false },
                { data: 'status', visible: false }
            );
            
            return columns;
        }
    },
    watch: {
        filterType(value) {
            this.getSprint(value);
        },
        loading(value) {
            if(value) {
                this.$emit('loading', true);
            } else {
                this.$emit('loading', false);
            }
        }
    },
    data: () => ({
        ready: false,

        issues: [],
        issueTypes: [],
        datatableOptions: { lengthMenu: [[10, 25, 50, -1], [10, 25, 50, "All"]] },
        loading: false,
        issueToEdit: null,
        commentsIssue: null,
        users: null,

        canReadComments: false,
        canWriteComments: false,

        canReadTimeTracking: false,
        canWriteTimeTracking: false
    }),
    props: {
        filterType: {
            type: Number,
            default: null
        }
    }
};
</script>

<style lang="scss" scoped>

</style>
